/**
 * QMP-FE-6 (F1) — STEP 7 "APPROVALS", THE PURE PART. No React, no api.
 *
 * WHAT THIS FILE IS ALLOWED TO DO. It reshapes what the server already
 * decided so a screen can print it. It decides NOTHING:
 *
 *   * the status of a company on a package is `approvals[].status` — the five
 *     words the contract serves (`approved`, `not_affected`, `objected`,
 *     `awaiting`, `overdue`), never recomputed here;
 *   * QMP-FE-8a (D30) — which packages the company approves in ONE act is the
 *     server's `approval_act.pending`, sent back as it came (`actBody`);
 *     nothing here groups or filters it;
 *   * which rules were struck through is `diff_from_previous[q].sections_changed`
 *     (D14.10); `previous_rules_hash: null` means the package is new and
 *     nothing is struck;
 *   * `overdue` IS A LABEL. Nothing here turns time into an approval, and no
 *     sentence in this file may suggest it does (D11.9).
 *
 * The sheet is `lib/qmpReview.sheetRows` — the SAME sheet step 6 shows, fed
 * with what the revision froze instead of the draft.
 */
import { sheetRows } from './qmpReview';
import { canonical, RECEIVER_LIMIT_LABEL, formatDuration } from './qmpRules';
import { fmtShortDay } from './utils';

/** The sections of a package rule, in the order the sheet prints them, and
 *  the sheet label each one lands on (`sheetRows`, `lib/qmpReview`). A label
 *  with no section — "Parties" — is never struck: a swap of a named company
 *  bumps `structure_version` and expires the approvals outright. */
export const SECTION_LABEL = {
  itp_rules: 'ITPs',
  hold_points: 'Hold points',
  witness_points: 'Witness points',
  handover: 'Handover',
  punch_list: 'Punch list',
  ncr: 'NCRs',
  rfi: 'RFIs',
  visibility: 'Visibility',
  ai: 'AI drafting',
};

/** A section that prints more than one row of the sheet: `handover` also
 *  prints the receiving company's time to sign (§19, QMP-FE-7). */
export const SECTION_MORE_LABELS = {
  handover: [RECEIVER_LIMIT_LABEL],
};

/** The five statuses the contract serves, and nothing else. */
export const STATUSES = ['approved', 'not_affected', 'objected', 'awaiting', 'overdue'];

/** `roles[]` of the contract -> the words the mockup prints (v79 `approvers`). */
export const ROLE_LABEL = {
  execution: 'Executing',
  verification: 'Verification',
  acceptance: 'Acceptance',
  itp_author: 'ITP author',
  endorsing: 'endorsing',
};
export const roleLine = (roles) => (roles || []).map((r) => ROLE_LABEL[r] || r).join(' · ');

// ---------------------------------------------------------------------------
// dates
// ---------------------------------------------------------------------------

/** "Fri 18 Sep 2026" — DATES-1: the app's one day (`fmtShortDay`), always with
 *  the year (a `{ withYear }` a caller still passes changes nothing). A
 *  `YYYY-MM-DD` — `due_at` is one — is read as the calendar day the server
 *  named, never through a timezone that could move it; an instant
 *  (`decided_at`) is read on `TIMEZONE`'s clock. */
export function formatDay(value) {
  return fmtShortDay(value);
}

// ---------------------------------------------------------------------------
// one company on one package
// ---------------------------------------------------------------------------

/** The chip of one company's row: `{text, tone}`.
 *  `tone`: `ok` | `warn` | `bad` | `dim` — the mockup's `.chip.ok`, `.chip.tba`,
 *  the red inline chip, and `.chip.dimc`. Every sentence is the mockup's. */
export function statusChip(row) {
  const status = row && row.status;
  if (status === 'approved') {
    return { text: `Approved · ${formatDay(row.decided_at)}`.replace(/ · $/, ''), tone: 'ok' };
  }
  if (status === 'not_affected') {
    return { text: 'Not affected · earlier approval still stands', tone: 'dim' };
  }
  if (status === 'objected') {
    return { text: `Objection raised · ${formatDay(row.decided_at)}`.replace(/ · $/, ''), tone: 'bad' };
  }
  // A LABEL, and only a label: the deadline passed and nobody answered.
  if (status === 'overdue') {
    return { text: row.due_at ? `Overdue — Due ${formatDay(row.due_at)}` : 'Overdue', tone: 'bad' };
  }
  return {
    text: row && row.due_at ? `Waiting for approval · Due ${formatDay(row.due_at)}` : 'Waiting for approval',
    tone: 'warn',
  };
}

/** The `last_record` line — "they approved Revision 2 and have not answered
 *  Revision 3" is exactly what the contract keeps this for, so it is shown
 *  even when the status went back to `awaiting`. `null` when there is none.
 *  The reason of an objection is its own line (`objectionReason`). */
export function lastRecordLine(row) {
  const rec = row && row.last_record;
  if (!rec || !rec.decided_at) return null;
  const verb = rec.decision === 'objected' ? 'objected' : 'approved';
  const who = rec.user_name || '—';
  const superseded = row.status === 'awaiting' || row.status === 'overdue';
  return {
    text: `${who} ${verb} on ${formatDay(rec.decided_at, { withYear: true })}`,
    superseded,
  };
}

/** QMP-FE-7 — the reason of an objection, as the server wrote it, or `null`.
 *  Only when the last thing this company said WAS an objection and the text
 *  is not empty: an approval owes no words, and no empty "Reason:" is drawn. */
export function objectionReason(row) {
  const rec = row && row.last_record;
  if (!rec || rec.decision !== 'objected') return null;
  const reason = typeof rec.reason === 'string' ? rec.reason : '';
  return reason.trim() ? reason : null;
}

/** QMP-FE-7 — may the person reading decide THIS row? The server's answer
 *  (`can_decide`, from the same function its door raises from) and nothing
 *  else: absent, `null` or anything but `true` is no. */
export const canDecide = (row) => Boolean(row) && row.can_decide === true;

/** Has this row's company answered, and does its answer stand? */
export const isSettled = (row) => Boolean(row)
  && (row.status === 'approved' || row.status === 'not_affected');

/** The row the reader may still answer on this package, or `null`. */
export const decidableRow = (entry) => ((entry && entry.approvals) || []).find(
  (r) => canDecide(r) && !isSettled(r),
) || null;

// ---------------------------------------------------------------------------
// one package
// ---------------------------------------------------------------------------

const rowsOf = (entry) => (entry && entry.approvals) || [];

/** The Status cell of a package (v79:1387-1392): `{text, tone, objections}`.
 *  Counted over the rows the server served for this package — the same rows
 *  the detail prints, so the cell and the list can never disagree. */
export function packageStatus(entry) {
  const rows = rowsOf(entry);
  if (!rows.length) return null;
  const settled = rows.filter((r) => r.status === 'approved' || r.status === 'not_affected');
  const objections = rows.filter((r) => r.status === 'objected').length;
  if (rows.every((r) => r.status === 'not_affected')) {
    return { text: 'Not affected · earlier approvals remain valid', tone: 'dim', objections: 0 };
  }
  if (settled.length === rows.length) {
    return { text: 'All companies approved', tone: 'ok', objections: 0 };
  }
  return {
    text: `${rows.length - settled.length} of ${rows.length} companies still to approve`,
    tone: 'warn',
    objections,
  };
}

/** Has THIS company (a participation id) still to answer for this package? */
export const isPending = (entry, participationId) => rowsOf(entry).some(
  (r) => r.participation_id === participationId
    && r.status !== 'approved' && r.status !== 'not_affected',
);

/** The row of one company on one package, or `null`. */
export const rowOf = (entry, participationId) => rowsOf(entry).find(
  (r) => r.participation_id === participationId,
) || null;

// ---------------------------------------------------------------------------
// QMP-FE-8a — THE ONE ACT OF THE READER'S COMPANY (D30, decision 140 A)
// ---------------------------------------------------------------------------

/** "1 QWP" / "3 QWPs" (mockup v83 `plural`, l. 1641). */
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const isText = (v) => typeof v === 'string' && v.length > 0;
const scopeEntryOk = (p) => Boolean(p) && typeof p === 'object' && isText(p.qwp_id)
  && isText(p.rules_hash) && Number.isInteger(p.structure_version);

/** `approval_act` of `GET /qmp`, read and NEVER recomputed, filtered or
 *  regrouped: `null` unless the server served an object with a `pending` list.
 *  `pending` keeps every entry as it came; `valid` is false when ANY entry
 *  lacks a `qwp_id`, a `rules_hash` or an integer `structure_version`, an id
 *  repeats, or the revision / participation is missing — the screen then
 *  locks the act and says why (`actProblem`), it never drops the bad entry
 *  and signs the rest. `canDecide` is the server's `can_decide`, and only
 *  `true` is yes. */
export function readAct(act) {
  if (!act || typeof act !== 'object' || !Array.isArray(act.pending)) return null;
  const pending = act.pending.slice();
  const ids = pending.map((p) => (p && p.qwp_id));
  const valid = Number.isInteger(act.revision) && act.revision > 0
    && isText(act.participation_id)
    && pending.every(scopeEntryOk)
    && new Set(ids).size === ids.length;
  return {
    revision: Number(act.revision || 0),
    participationId: act.participation_id || null,
    pending,
    canDecide: act.can_decide === true,
    valid,
  };
}

/** Does the server offer the reader an act to sign now? Its `can_decide` and
 *  something pending — nothing the screen worked out. */
export const actOpen = (act) => Boolean(act) && act.canDecide && act.pending.length > 0;

/** The pending packages as the page prints them: structure order (the server
 *  serves `pending` in `qwp_id` order), one structure row each. A pending id
 *  the structure does not carry is kept, by its id — `actProblem` then locks
 *  the act, because there is no sheet to show for it. */
export function actRows(structure, act) {
  if (!act) return [];
  const ids = new Set(act.pending.filter(scopeEntryOk).map((p) => p.qwp_id));
  const rows = (structure || []).filter((r) => ids.has(r.qwp_id));
  const known = new Set(rows.map((r) => r.qwp_id));
  act.pending.forEach((p) => {
    if (scopeEntryOk(p) && !known.has(p.qwp_id)) rows.push({ qwp_id: p.qwp_id, name: p.qwp_id });
  });
  return rows;
}

/** Why the act cannot be signed on this page, or `null` when it can.
 *
 *  `missing` — no act was served; `malformed` — the act is not whole
 *  (`readAct().valid`); `sheet` — a pending package has no sheet on this page
 *  that belongs to the act: no structure row, another live
 *  `structure_version`, no published rules, or published rules of another
 *  revision or another `rules_hash`. What is SHOWN and what is SENT must be
 *  the same thing (the same `GET /qmp`, the act's revision): when they cannot
 *  be matched, nothing is signed. */
export function actProblem(act, structure, packages) {
  if (!act) return 'missing';
  if (!act.valid) return 'malformed';
  const rows = structure || [];
  const bad = act.pending.some((p) => {
    const row = rows.find((r) => r.qwp_id === p.qwp_id);
    const current = packages && packages[p.qwp_id] && packages[p.qwp_id].current;
    return !row || row.structure_version !== p.structure_version
      || !current || Number(current.revision) !== act.revision
      || current.rules_hash !== p.rules_hash;
  });
  return bad ? 'sheet' : null;
}

/** The sentence of a locked act (`actProblem`). */
export const ACT_PROBLEM = {
  missing: 'The QWPs waiting for your company’s approval were not served with this page, so approving is locked. Reload the page to try again.',
  malformed: 'The QWPs waiting for your company’s approval could not be read, so approving is locked. Reload the page to try again.',
  sheet: 'The rules on this page do not match what is waiting for your company’s approval, so approving is locked. Reload the page to review the current rules.',
};

/** The context a decision belongs to: the project, the company, the revision.
 *  A result that comes back for another context is not applied, and a change
 *  of context clears the tick and every objection being written. */
export const decisionContext = ({ projectId, participationId, revision }) => JSON.stringify([
  projectId || null, participationId || null, Number(revision || 0),
]);

/** What the "Before you sign" tick is bound to: the context, the scope of the
 *  act exactly as served (each `qwp_id`, `rules_hash`, `structure_version`)
 *  and the version of the statement. Any change — a reload that moved the
 *  act, an objection that took a QWP out, another company — is another key,
 *  and a tick given for one key never counts for another. */
export const signingKey = ({
  projectId, participationId, revision, act, statementVersion,
}) => JSON.stringify([
  decisionContext({ projectId, participationId, revision }),
  act ? act.pending.map((p) => [p && p.qwp_id, p && p.rules_hash, p && p.structure_version]) : null,
  statementVersion === undefined ? null : statementVersion,
]);

/** The body of the one act: the approval body, the packages, and WHAT THE PAGE
 *  SHOWED — `scope` (each pending entry's `qwp_id`, `rules_hash`,
 *  `structure_version`, copied as served, in the order served — those three
 *  keys and no other) and `participation_id`. The door refuses anything else
 *  (422 `APPROVAL_SCOPE_REQUIRED` / 409 `APPROVAL_SCOPE_CHANGED`). */
export function actBody(act, { guideVersion }) {
  const scope = act.pending.map((p) => ({
    qwp_id: p.qwp_id, rules_hash: p.rules_hash, structure_version: p.structure_version,
  }));
  return {
    ...decisionBody({ revision: act.revision, decision: 'approved', guideVersion }),
    qwp_ids: scope.map((s) => s.qwp_id),
    scope,
    participation_id: act.participationId,
  };
}

/** QMP-FE-8a — no response at all (a timeout, a lost connection): nobody
 *  knows whether the door wrote anything. Never read as a refusal. */
export const outcomeUnknown = (err) => !(err && err.response);

/** The two outcomes that are not a refusal (QMP-FE-8a): the door answered 201
 *  but the plan could not be read again (`recorded`), and no answer came back
 *  at all — a timeout or a lost connection (`unknown`). Neither is retried:
 *  the page asks for a reload first. */
export const ACT_OUTCOME = {
  recorded: {
    approved: 'Your approval was recorded. Reload to see the current state.',
    objected: 'Your objection was recorded. Reload to see the current state.',
  },
  unknown: {
    approved: 'We could not confirm whether your approval was recorded. Reload before trying again.',
    objected: 'We could not confirm whether your objection was recorded. Reload before trying again.',
  },
};

// ---------------------------------------------------------------------------
// the sheet, with the old rule struck through beside the new (D14.10)
// ---------------------------------------------------------------------------

const has = (o, k) => Boolean(o) && Object.prototype.hasOwnProperty.call(o, k);

/** What a REVISION froze for a package, in the shape `sheetRows` reads.
 *  Step 6 shows the draft; step 7 shows what was published — the thing that
 *  is actually being approved. `null` (no rules) is a sheet of "—". */
export function revisionRules(rules) {
  const out = {};
  Object.keys(SECTION_LABEL).forEach((section) => {
    out[section] = has(rules, section) ? { set: true, value: rules[section] } : { set: false };
  });
  return out;
}

/** The sheet of one package as the approver reads it.
 *
 *  `[{label, lines, was}]` — `lines` is the rule being approved and `was`, when
 *  present, the same row as the PREVIOUS revision left it: the screen prints it
 *  struck through beside the new one. A row is only marked when the contract
 *  says that section changed (`sectionsChanged`) AND a previous revision
 *  covered the package. First publication: `was` is never set.
 *
 *  A `sectionsChanged` entry whose two forms are identical (nothing a person
 *  would see moved) is not marked — the strike-through is for what a reader
 *  must notice, not for a reordered dict. */
export function approvalSheet(row, {
  rules, previousRules, sectionsChanged, previousRulesHash, companyNames,
}) {
  const now = sheetRows(row, revisionRules(rules), companyNames);
  const first = !previousRulesHash;
  if (first || !sectionsChanged || !sectionsChanged.length) {
    return now.map((r) => ({ ...r, was: null }));
  }
  const before = sheetRows(row, revisionRules(previousRules), companyNames);
  const changedLabels = new Set(
    sectionsChanged.flatMap((s) => [SECTION_LABEL[s]].concat(SECTION_MORE_LABELS[s] || []))
      .filter(Boolean),
  );
  return now.map((r, i) => {
    if (!changedLabels.has(r.label)) return { ...r, was: null };
    const old = before[i] && before[i].label === r.label
      ? before[i]
      : before.find((b) => b.label === r.label);
    if (!old || canonical(old.lines) === canonical(r.lines)) return { ...r, was: null };
    return { ...r, was: old.lines };
  });
}

/** The latest moment a company answered, across every package of the plan.
 *  DERIVED FROM WHAT IS ALREADY ON THE PAGE (`approvals[].decided_at`) and
 *  nothing else: the contract has no "approved on" for a revision, and there
 *  is no door that lists approval records. `null` when no row carries a date,
 *  and the sentence then simply leaves the date out. */
export function lastApprovalAt(packages) {
  let latest = null;
  Object.values(packages || {}).forEach((entry) => {
    ((entry && entry.approvals) || []).forEach((row) => {
      if (row.status !== 'approved' || !row.decided_at) return;
      if (!latest || row.decided_at > latest) latest = row.decided_at;
    });
  });
  return latest;
}

// ---------------------------------------------------------------------------
// the approval history of one package (QMP-FE-7, §16)
// ---------------------------------------------------------------------------

/** `records[]` of `GET .../packages/{q}/approvals`, grouped by revision:
 *  `[{revision, records: [...]}]`. The server's order is kept — newest
 *  revision first, and within one revision as it came. Nothing is counted
 *  or summarised: the contract serves records, not "approved by all". */
export function historyByRevision(records) {
  const groups = [];
  (records || []).forEach((rec) => {
    if (!rec) return;
    const n = Number(rec.revision || 0);
    const last = groups[groups.length - 1];
    if (last && last.revision === n) last.records.push(rec);
    else groups.push({ revision: n, records: [rec] });
  });
  return groups;
}

/** One record as a line: "<company> · <roles> — <approved|objected> by
 *  <person> · <d Mmm yyyy>", plus " · one act over several packages" when the
 *  record was part of a lot (`batch_id`). The reason is its own line
 *  (`recordReason`). */
export function historyLine(rec) {
  if (!rec) return '';
  const company = rec.participation_name || '—';
  const roles = roleLine(rec.roles);
  const verb = rec.decision === 'objected' ? 'objected' : 'approved';
  const who = rec.user_name || '—';
  const when = formatDay(rec.decided_at, { withYear: true });
  return `${company}${roles ? ` · ${roles}` : ''} — ${verb} by ${who}`
    + `${when ? ` · ${when}` : ''}${rec.batch_id ? ' · one act over several packages' : ''}`;
}

/** The reason of one history record: only an objection, never empty. */
export function recordReason(rec) {
  if (!rec || rec.decision !== 'objected') return null;
  const reason = typeof rec.reason === 'string' ? rec.reason : '';
  return reason.trim() ? reason : null;
}

export const HISTORY_FALLBACK = 'Could not read the approval history';

/** How a failed read of the history is shown: `hidden` (404 — outside the
 *  fence, the section does not exist for this reader) or `retry` (503 and
 *  anything else — "Try again"), with the server's sentence when it gave one. */
export function describeHistoryError(err) {
  const status = err && err.response ? err.response.status : null;
  if (status === 404) return { kind: 'hidden', message: '' };
  const detail = err && err.response && err.response.data ? err.response.data.detail : null;
  const message = (typeof detail === 'string' && detail)
    || (detail && detail.message) || HISTORY_FALLBACK;
  return { kind: 'retry', message };
}

// ---------------------------------------------------------------------------
// the bodies the doors read
// ---------------------------------------------------------------------------

/** The body of one decision. `reason` travels only with an objection, and the
 *  ceiling is the record's (`QMP_APPROVAL_REASON_MAX`).
 *
 *  CLEAN-FE-2 — an objection is not a signature: it carries no acknowledgement
 *  of the Before you sign statement (the server ignores one and records none,
 *  CLEAN-BE-1 B4). The acknowledgement travels with an approval, as before. */
export const REASON_MAX = 2000;

export function decisionBody({
  revision, decision, reason, guideVersion,
}) {
  const body = {
    revision: Number(revision),
    decision,
  };
  if (decision === 'objected') {
    body.reason = (reason || '').slice(0, REASON_MAX);
  } else {
    body.guide_acknowledged = true;
    body.guide_version = guideVersion;
  }
  return body;
}

// ---------------------------------------------------------------------------
// refusals — the server's words, put in the right place
// ---------------------------------------------------------------------------

export const APPROVAL_FALLBACK = 'Could not record the decision';

/** `{code, message, qwpIds}` from an axios error of any of the three doors.
 *  The sentence is ALWAYS the server's `detail.message`; the code only says
 *  WHERE it belongs and whether the page must be read again. */
export function describeApprovalError(err, fallback = APPROVAL_FALLBACK) {
  const detail = err && err.response && err.response.data
    ? err.response.data.detail : null;
  if (typeof detail === 'string' && detail) {
    return { code: null, message: detail, qwpIds: [], reload: false };
  }
  const code = (detail && detail.code) || null;
  const message = (detail && detail.message) || fallback;
  const qwpIds = (detail && (detail.qwp_ids || (detail.qwp_id ? [detail.qwp_id] : []))) || [];
  // The plan moved under the page: what is on screen is not what would be
  // signed, so the only honest next step is to read it again.
  return {
    code,
    message,
    qwpIds,
    reload: code === 'REVISION_NOT_CURRENT' || code === 'APPROVAL_SCOPE_CHANGED',
  };
}

/** Where a refusal is shown: beside the guide tick, beside the reason box, or
 *  on the row/act that was refused. */
export function errorPlace(code) {
  if (code === 'GUIDE_ACKNOWLEDGEMENT_REQUIRED') return 'ack';
  if (code === 'REASON_REQUIRED') return 'reason';
  return 'act';
}

// ---------------------------------------------------------------------------
// QMP-FE-8g — THE v83 APPROVALS PAGE (`mockup-qmp-v83-official.html`,
// `approvalPage` l. 2086-2131): the status line, the table "Quality Work
// Package · Approvals needed · Agreement · Details", and the agreement panel.
// Every word is read off what `GET /qmp` serves (`approval_due`,
// `approvals_due_at`, `packages[q].approvals[]`) and `diff_from_previous` of
// the revision; nothing here decides who agreed.
// ---------------------------------------------------------------------------

const settledStatus = (s) => s === 'approved' || s === 'not_affected';
const joinAnd = (names) => names.join(' and ');

/** v83 status line, left: "Approval due: 5 working days · by Fri 30 Oct 2026".
 *  The duration is the server's fixed `approval_due` (decision 133 A); the day
 *  is `approvals_due_at` — a LABEL, time never approves. No day served, no
 *  "· by". */
export function dueLine(approvalDue, dueAt) {
  const by = dueAt ? formatDay(dueAt) : '';
  return `Approval due: ${formatDuration(approvalDue) || '—'}${by ? ` · by ${by}` : ''}`;
}

/** v83 `askedCompanies`: the companies this revision asked — every company
 *  with a row the revision did not carry over (`not_affected`), counted once
 *  across the packages served. */
export function companiesAsked(packages) {
  const ids = new Set();
  Object.values(packages || {}).forEach((entry) => {
    rowsOf(entry).forEach((r) => {
      if (r && r.participation_id && r.status !== 'not_affected') ids.add(r.participation_id);
    });
  });
  return ids.size;
}

/** v83 status line, right: "3 companies asked". */
export const askedLine = (n) => `${plural(n, 'company', 'companies')} asked`;

/** Did Revision `revision` change this package? From `diff_from_previous[q]`
 *  (D14.10): a package the previous revision did not cover
 *  (`previous_rules_hash: null`) or with a section named is changed; `null`
 *  when there is no previous revision or the diff was not read — never a
 *  guess. */
export function changedIn(d, revision) {
  if (!(Number(revision) > 1) || !d) return null;
  return !d.previous_rules_hash || (d.sections_changed || []).length > 0;
}

/** v83 under the QWP name: "Changed in Revision 2" / "No change in Revision 2";
 *  '' on Revision 1 or without the diff. */
export function changeLabel(d, revision) {
  const changed = changedIn(d, revision);
  if (changed === null) return '';
  return `${changed ? 'Changed' : 'No change'} in Revision ${revision}`;
}

/** v83 intro, from Revision 2: "Revision 2 changed 1 QWP; the others keep
 *  their earlier approvals." '' when the diff was not read. */
export function changedSentence(diff, structure, revision) {
  if (!(Number(revision) > 1) || !diff || !Object.keys(diff).length) return '';
  const n = (structure || []).filter((r) => changedIn(diff[r.qwp_id], revision) === true).length;
  return ` Revision ${revision} changed ${plural(n, 'QWP', 'QWPs')}; the others keep their earlier approvals.`;
}

/** The request line of a company (NL21-A10.3): the person named for it, or
 *  its Company Representatives. AN ADDRESS, NEVER AN AUTHORITY. */
export function requestText(row) {
  const nominee = row && row.nominee;
  if (!nominee) return 'No approver selected';
  const recipients = (row && row.recipients) || [];
  const extra = recipients.length > 1 ? ` +${recipients.length - 1}` : '';
  const who = nominee.name || '—';
  return nominee.source === 'company_representative'
    ? `Request goes to ${who}${extra} · Company Representative`
    : `Request goes to ${who}`;
}

/** v83 — the revision record of a carried approval is not served
 *  (`last_record` has no revision), so the line names no number. */
export const CARRIED_LINE = 'Approval from an earlier revision still valid';

/** One company in the "Approvals needed" cell (v83 l. 2097):
 *  `{text, tone, source}` — Approved / Pending / Objected, and under it the
 *  request line while pending, or the carried line. `overdue` is Pending: the
 *  label is the server's and changes nothing. */
export function partyState(row) {
  const status = row && row.status;
  if (status === 'approved') return { text: 'Approved', tone: 'good', source: '' };
  if (status === 'not_affected') return { text: 'Approved', tone: 'good', source: CARRIED_LINE };
  if (status === 'objected') return { text: 'Objected', tone: 'bad', source: '' };
  return { text: 'Pending', tone: 'warn', source: requestText(row) };
}

/** The "Agreement" cell of a package (v83 `packageAgreement`, l. 2093-2096):
 *  `{state, text, tone, detail}`, or `null` for a package with no rows. */
export function agreementCell(entry, revision) {
  const rows = rowsOf(entry);
  if (!rows.length) return null;
  const objected = rows.filter((r) => r.status === 'objected').map((r) => r.name);
  const pending = rows.filter((r) => !settledStatus(r.status) && r.status !== 'objected').map((r) => r.name);
  if (!objected.length && !pending.length) {
    const carried = Number(revision) > 1 && rows.every((r) => r.status === 'not_affected');
    return {
      state: 'agreed',
      text: 'Rules agreed',
      tone: 'good',
      detail: carried
        ? `Unchanged since Revision ${revision - 1} · approvals carried over`
        : `No sign-offs paused · Revision ${revision}`,
    };
  }
  if (objected.length) {
    return {
      state: 'objected', text: 'Objection under review', tone: 'bad', detail: `Objection: ${objected.join(', ')}`,
    };
  }
  return {
    state: 'waiting', text: 'Waiting for approval', tone: 'warn', detail: `Waiting for: ${pending.join(', ')}`,
  };
}

/** The state box of the agreement panel (v83 l. 2109): `{agreed, head, body}`. */
export function agreementBox(entry) {
  const rows = rowsOf(entry);
  if (!rows.length) return null;
  const objected = rows.filter((r) => r.status === 'objected').map((r) => r.name);
  const pending = rows.filter((r) => !settledStatus(r.status) && r.status !== 'objected').map((r) => r.name);
  if (!objected.length && !pending.length) {
    return {
      agreed: true,
      head: 'Every company named in this package has approved the current rules.',
      body: 'No sign-offs are paused here because of QMP approval.',
    };
  }
  const waiting = objected.length ? objected : pending;
  const one = waiting.length === 1;
  return {
    agreed: false,
    head: 'Agreement in progress.',
    body: `Until ${joinAnd(waiting)} approve${one ? 's' : ''}, only ${one ? 'that company’s' : 'those companies’'}`
      + ' QA sign-offs in this package are paused.',
  };
}

/** The fixed sentences of the page (v83 l. 2087, 2124, 2127, 2112). */
export const APPROVALS_COPY = {
  draftTitle: 'Company approvals',
  draftHead: 'The QMP is still a draft.',
  draftBody: 'No approval has been requested yet.',
  nextTitle: 'Next step',
  nextBody: 'Review the items listed in step 5 and publish the first revision. Then each company sees the QWPs where it has a role.',
  goPublish: 'Go to Review & publish',
  intro: 'Each company checks the rules for the packages where it has a role and approves them — or objects, with a reason. A QWP’s rules are agreed when every company named in it has approved.',
  info: 'Until a company approves, only that company’s QA sign-offs are paused in that QWP. Other QWPs and other companies are not affected. Time never approves the QMP automatically.',
  note: 'This agreement is about the quality rules. Execution, handover acceptance and QA Complete keep their own states.',
};

/** v83 h2 once published: "Revision 2 · agreement per QWP". */
export const pageTitle = (revision) => `Revision ${revision} · agreement per QWP`;
